import { isSafari } from '@intelligence/utils/common';
import { isNotEmpty } from '@intelligence/v3/utils/common';

/**
 * 判断是否为外部链接（第三方站点）
 * 规则：
 * 1. 空值返回 false
 * 2. 以 / 开头的为内部路由（协议相对URL除外）
 * 3. 以 # 开头的锚点链接为内部链接
 * 4. 以 ./ 或 ../ 开头的相对路径为内部链接
 * 5. 协议相对URL（//开头）为外部链接
 * 6. 完整URL（http:// 或 https://）根据域名判断是否为外部链接
 */
export const isExternalUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // 去除前后空格
  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return false;
  }

  // 锚点链接，内部链接
  if (trimmedUrl.startsWith('#')) {
    return false;
  }

  // 协议相对URL（//example.com），外部链接
  if (trimmedUrl.startsWith('//')) {
    return true;
  }

  // 相对路径（./path 或 ../path），内部链接
  if (trimmedUrl.startsWith('./') || trimmedUrl.startsWith('../')) {
    return false;
  }

  // 以 / 开头的绝对路径，内部路由
  if (trimmedUrl.startsWith('/')) {
    return false;
  }

  // 完整URL（http:// 或 https://）
  if (/^https?:\/\//i.test(trimmedUrl)) {
    try {
      const urlObj = new URL(trimmedUrl);
      const currentHost = window.location.host;
      // 判断域名是否相同（包含端口）
      return urlObj.host !== currentHost;
    } catch {
      // URL解析失败，保守处理为外部链接
      return true;
    }
  }

  // 其他情况（如纯文本路径），保守处理为外部链接
  return true;
};

/**
 * 构建带查询参数的 URL，将 filters 转换为 query string
 * 支持外部链接和内部路由
 * @param baseUrl - 基础URL
 * @param filters - 查询参数对象
 * @returns 构建后的完整URL
 */
export const buildExternalUrl = (baseUrl: string, filters: IRecord): string => {
  // 空值处理
  if (!baseUrl || typeof baseUrl !== 'string') {
    return '';
  }

  const trimmedBaseUrl = baseUrl.trim();
  if (!trimmedBaseUrl) {
    return '';
  }

  // 无参数直接返回
  if (!filters || Object.keys(filters).length === 0) {
    return trimmedBaseUrl;
  }

  // 构建查询字符串
  const queryString = Object.entries(filters)
    .filter(([key, value]) => isNotEmpty(key) && isNotEmpty(value))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

  if (!queryString) {
    return trimmedBaseUrl;
  }

  // 处理 hash 片段：查询参数应放在 hash 之前
  // 例如：https://example.com/path#section -> https://example.com/path?a=1#section
  const hashIndex = trimmedBaseUrl.indexOf('#');
  let urlWithoutHash = trimmedBaseUrl;
  let hash = '';

  if (hashIndex !== -1) {
    urlWithoutHash = trimmedBaseUrl.substring(0, hashIndex);
    hash = trimmedBaseUrl.substring(hashIndex);
  }

  // 判断分隔符
  const separator = urlWithoutHash.includes('?') ? '&' : '?';

  return `${urlWithoutHash}${separator}${queryString}${hash}`;
};

/**
 * 在新标签页打开 URL（兼容 Safari 浏览器弹窗拦截问题）
 * Safari 浏览器要求 window.open 必须在用户交互事件的同步调用栈中执行
 * 异步调用时会被拦截，因此先打开空白窗口再设置 location
 * @param url - 目标 URL
 * @returns 是否成功打开
 */
export const openInNewTab = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return false;
  }

  if (isSafari()) {
    // Safari：先打开空白窗口，再设置 location（解决异步调用被拦截问题）
    const page = window.open('', '_blank');
    if (page) {
      page.location.href = trimmedUrl;
      return true;
    }
    return false;
  }

  // 其他浏览器直接打开
  const page = window.open(trimmedUrl, '_blank');
  return !!page;
};
